import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, List, Space, Tag, Typography, message } from 'antd';
import { space } from '@viq-admin/ui';
import { CRM_COPY } from './copy';
import { HelpHint, HintLine } from './HelpHint';
import { byDisplayName } from './sortRows';
import { CRM_EMAIL_SHAPE } from './emailShape';
import type { CrmQueueApi, PersonMatchState } from './types';

export interface CleanViewProps {
  conferenceId: string;
  api: CrmQueueApi;
  source: string;
  /** Full match-check results — this view filters to `needs_email` itself. */
  results: PersonMatchState[];
  /**
   * Timestamp of the parent's last COMPLETED match-check fetch (react-query
   * `dataUpdatedAt`). A refetch whose row did NOT graduate returns
   * byte-identical results (a needs_email row's email is null either way), so
   * this advancing is the only signal that the re-check actually ran.
   */
  resultsUpdatedAt: number;
  /** Parent invalidates the match-check query; the backend's computed
   *  staleness re-checks the row and it auto-graduates out of Clean. */
  onRowsChanged: () => void;
}

/** The CRM engine's acceptance shape (see `emailShape.ts`; must stay in lockstep). */
const EMAIL_SHAPE = CRM_EMAIL_SHAPE;

function withId(set: Set<string>, id: string): Set<string> {
  if (set.has(id)) return set;
  const next = new Set(set);
  next.add(id);
  return next;
}

function withoutId(set: Set<string>, id: string): Set<string> {
  if (!set.has(id)) return set;
  const next = new Set(set);
  next.delete(id);
  return next;
}

/**
 * Clean view (p3 Task 3) — the needs-email queue. Every row is a person the
 * merge engine can't check against the CRM yet because there's no email on
 * file. Inline fix + save graduates the row out of Clean on the next
 * match-check; the muted `enrichSeam` tag is the visible seam an automated
 * enrichment step plugs into later (today it's just "waiting"). Rows never
 * lock — a save in flight disables only that row's own controls, so the
 * operator can keep fixing other rows while one save is in progress. In-flight
 * and failed rows are tracked as SETS, not single slots: two saves can be in
 * flight at once and each keeps its own busy/error state (review fix).
 *
 * Without `api.canManage` the queue is still fully browsable — only the
 * inline-fix input and its Save button are disabled.
 */
export function CleanView({
  conferenceId,
  api,
  source,
  results,
  resultsUpdatedAt,
  onRowsChanged,
}: CleanViewProps) {
  // `.filter()` hands back a fresh array, so the in-place sort never reaches
  // the `results` prop.
  const rows = useMemo(
    () => results.filter((r) => r.bucket === 'needs_email').sort(byDisplayName),
    [results],
  );
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set());
  const [errorIds, setErrorIds] = useState<Set<string>>(new Set());
  // Rows that just saved successfully but are still present in `results`
  // because the parent's refetch (triggered by onRowsChanged) hasn't landed
  // yet — kept disabled so a slow refetch can't produce a duplicate submit.
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
  // Saved rows a COMPLETED refetch bounced straight back to needs_email —
  // the stored email didn't survive the engine's stricter check. Reopened for
  // another fix, with `savedStillNeedsEmail` in place of the saved note.
  const [bouncedIds, setBouncedIds] = useState<Set<string>>(new Set());

  // Once a saved (or bounced) row actually leaves `results` (the backend's
  // computed staleness graduated it out of Clean), drop its marker so the id
  // is free again if it's ever legitimately needs_email in the future.
  useEffect(() => {
    setSavedIds((prev) => {
      if (prev.size === 0) return prev;
      const stillPresent = new Set(rows.map((r) => r.personId));
      let changed = false;
      const next = new Set<string>();
      for (const id of prev) {
        if (stillPresent.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
    setBouncedIds((prev) => {
      if (prev.size === 0) return prev;
      const stillPresent = new Set(rows.map((r) => r.personId));
      let changed = false;
      const next = new Set<string>();
      for (const id of prev) {
        if (stillPresent.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [rows]);

  // A refetch that COMPLETED (`resultsUpdatedAt` advanced) with a saved row
  // still listed needs_email means the row did not graduate — un-stick it
  // rather than holding `savedPendingRefresh` forever. Guarded by the ref so
  // rows/savedIds changes without a fresh fetch never trip it.
  const lastResultsUpdatedAtRef = useRef(resultsUpdatedAt);
  useEffect(() => {
    if (resultsUpdatedAt === lastResultsUpdatedAtRef.current) return;
    lastResultsUpdatedAtRef.current = resultsUpdatedAt;
    if (savedIds.size === 0) return;
    const stillPresent = new Set(rows.map((r) => r.personId));
    const bounced: string[] = [];
    for (const id of savedIds) if (stillPresent.has(id)) bounced.push(id);
    if (bounced.length === 0) return;
    setSavedIds((prev) => {
      const next = new Set(prev);
      for (const id of bounced) next.delete(id);
      return next;
    });
    setBouncedIds((prev) => {
      const next = new Set(prev);
      for (const id of bounced) next.add(id);
      return next;
    });
  }, [resultsUpdatedAt, rows, savedIds]);

  async function save(personId: string) {
    if (!api.canManage) return; // controls are disabled; belt-and-braces
    if (savedIds.has(personId)) return; // guard: already saved, waiting on the refetch
    const trimmed = (drafts[personId] ?? '').trim();
    if (!EMAIL_SHAPE.test(trimmed)) return;
    setSavingIds((prev) => withId(prev, personId));
    setErrorIds((prev) => withoutId(prev, personId));
    setBouncedIds((prev) => withoutId(prev, personId));
    try {
      await api.updateLeadFields(personId, { email: trimmed });
      setSavedIds((prev) => withId(prev, personId));
      onRowsChanged();
    } catch {
      setErrorIds((prev) => withId(prev, personId));
      void message.error(CRM_COPY.saveEmailFailed);
    } finally {
      setSavingIds((prev) => withoutId(prev, personId));
    }
  }

  return (
    <div data-testid={`crm-clean-view-${conferenceId}-${source}`}>
      <HintLine text={CRM_COPY.help.tabClean.text} testId="crm-hint-tab-clean" />
      {/* The header's count text stays its own text node — the help trigger is
          a SIBLING of the Title, never a child, so `${header} (N)` queries and
          the heading's accessible name are both left exactly as they were. */}
      <Space size={space.xs} align="center" style={{ marginBottom: space.md }}>
        <Typography.Title level={5} style={{ margin: 0 }}>
          {`${CRM_COPY.needsEmailHeader} (${rows.length})`}
        </Typography.Title>
        <HelpHint {...CRM_COPY.help.cleanQueue} testId="crm-help-clean-queue" />
      </Space>
      {rows.length === 0 ? (
        <Typography.Text type="secondary">{CRM_COPY.cleanEmptyState}</Typography.Text>
      ) : (
        <List
          dataSource={rows}
          renderItem={(row) => {
            const draft = drafts[row.personId] ?? '';
            const valid = EMAIL_SHAPE.test(draft.trim());
            const shapeInvalid = draft.trim() !== '' && !valid;
            const busy = savingIds.has(row.personId);
            const saved = savedIds.has(row.personId);
            const bounced = bouncedIds.has(row.personId);
            const failed = errorIds.has(row.personId);
            const locked = busy || saved || !api.canManage;
            return (
              <List.Item key={row.personId}>
                <Space
                  direction="vertical"
                  size={space.xs}
                  style={{ width: '100%', opacity: saved ? 0.6 : 1 }}
                >
                  <Space size={space.sm}>
                    <Typography.Text strong>{row.displayName}</Typography.Text>
                    <Tag>{CRM_COPY.enrichSeam}</Tag>
                    <HelpHint
                      {...CRM_COPY.help.cleanWaiting}
                      testId={`crm-help-clean-waiting-${row.personId}`}
                    />
                  </Space>
                  <Typography.Text type="secondary">{CRM_COPY.needsEmailRow}</Typography.Text>
                  <Space size={space.sm}>
                    <Input
                      size="large"
                      // The row exists BECAUSE this is missing: the red
                      // outline says so at a glance (gh-31 §4.6) and clears
                      // the moment the draft looks like an address.
                      status={valid ? undefined : 'error'}
                      placeholder={CRM_COPY.addEmailPlaceholder}
                      value={draft}
                      disabled={locked}
                      onChange={(e) =>
                        setDrafts((prev) => ({ ...prev, [row.personId]: e.target.value }))
                      }
                      aria-label={`Email for ${row.displayName}`}
                      style={{ width: 260 }}
                    />
                    <Button
                      type="primary"
                      disabled={!valid || locked}
                      loading={busy}
                      onClick={() => void save(row.personId)}
                    >
                      {CRM_COPY.saveButton}
                    </Button>
                    <HelpHint
                      {...CRM_COPY.help.cleanSaveEmail}
                      testId={`crm-help-clean-save-${row.personId}`}
                    />
                  </Space>
                  {saved ? (
                    <Typography.Text type="secondary">{CRM_COPY.savedPendingRefresh}</Typography.Text>
                  ) : shapeInvalid ? (
                    <Typography.Text type="danger">{CRM_COPY.emailShapeInvalid}</Typography.Text>
                  ) : bounced ? (
                    // The one Clean message an operator can't act on without
                    // knowing WHY, hence the only per-row note carrying a hint.
                    <Space size={space.xs}>
                      <Typography.Text type="danger">{CRM_COPY.savedStillNeedsEmail}</Typography.Text>
                      <HelpHint
                        {...CRM_COPY.help.cleanSavedStillNeedsEmail}
                        testId={`crm-help-clean-bounced-${row.personId}`}
                      />
                    </Space>
                  ) : failed ? (
                    <Typography.Text type="danger">{CRM_COPY.saveEmailFailed}</Typography.Text>
                  ) : null}
                </Space>
              </List.Item>
            );
          }}
        />
      )}
    </div>
  );
}
